import React, { useContext } from 'react'
import styled, { css } from 'styled-components'
import GatsbyImage from 'gatsby-image'
import { LayoutContext } from '../../contexts'
import { desktopVW, mobileVW } from '../../styles'
import { useGlobal } from '../../hooks/useGlobal'

const DYNAMIC_LABEL_SEPARATOR = '--'

const ProductLabelLogo = ({ label = 'hidden', logo, children, className }) => {
  const { node_locale } = useContext(LayoutContext)

  const {
    product: {
      new: newText,
      comingSoon,
      twoPack,
      threePack,
      limitedEdition,
      specialEdition,
      giftWrapping,
      dynamicLabels,
    },
    blackFriday: { blackFridayThemeColor },
  } = useGlobal(['product', 'blackFriday'], node_locale)

  const ProductLabelSwitcher = ({ label }) => {
    switch (label) {
      case 'New':
        return <ProductLabel new>{newText}</ProductLabel>
      case 'Coming soon':
        return <ProductLabel comingSoon>{comingSoon}</ProductLabel>
      case '2-pack':
        return <ProductLabel twoPack>{twoPack}</ProductLabel>
      case '3-pack':
        return <ProductLabel new>{threePack}</ProductLabel>
      case 'Limited edition':
        return <ProductLabel limitedEdition>{limitedEdition}</ProductLabel>
      case 'Special edition':
        return <ProductLabel specialEdition>{specialEdition}</ProductLabel>
      case 'Gift wrapping':
        return <ProductLabel giftWrapping>{giftWrapping}</ProductLabel>
      case 'Percentage':
        return (
          <ProductLabel
            percentage
            bgColor={blackFridayThemeColor || '#ff0058'}
            className={className}
          >
            {children}
          </ProductLabel>
        )
      case 'Logo':
        if (logo && logo.title === 'Ontwerp zonder titel (33)') {
          // Hide the gift logo for US and GB
          return node_locale !== 'en-US' && node_locale !== 'en-GB' && logo ? (
            <ProductLogo fluid={logo.fluid} alt={logo.title} />
          ) : null
        } else {
          return logo ? (
            <ProductLogo fluid={logo.fluid} alt={logo.title} />
          ) : null
        }
      case 'hidden':
        return null
      default:
        const getNewLabel = dynamicLabels.find((it) => it.match(label))
        const newLabel = getNewLabel?.split(DYNAMIC_LABEL_SEPARATOR)[1]?.trim()
        return newLabel ? <ProductLabel new>{newLabel}</ProductLabel> : null
    }
  }

  return <ProductLabelSwitcher label={label} />
}

export default ProductLabelLogo

const ProductLogo = styled(GatsbyImage)`
  width: ${mobileVW(40)};
  height: ${mobileVW(40)};
  z-index: 10;
  border-radius: 50%;
  margin-top: ${mobileVW(10)};
  margin-right: ${mobileVW(10)};

  .featured-collection & {
    top: -${(props) => props.theme.space.marginMobile}vw;
    right: -${(props) => props.theme.space.marginMobile}vw;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin-top: ${desktopVW(15)};
    margin-right: ${desktopVW(15)};
    width: ${desktopVW(80)};
    height: ${desktopVW(80)};
  }
`

const ProductLabel = styled.div`
  top: 3vw;
  right: 2vw;
  padding: 0 ${mobileVW(13)};
  background: ${(props) =>
    props.new ||
    props.comingSoon ||
    props.limitedEdition ||
    props.twoPack ||
    props.specialEdition ||
    props.giftWrapping
      ? props.theme.colors.yellow
      : props.theme.colors.blue};
  font-size: ${(props) => props.theme.fontSize.m18};
  /* Percentage = Black Friday */
  ${(props) =>
    props.percentage &&
    css`
      padding: 2px ${mobileVW(13)};
      background-color: ${props.bgColor}};
      font-size: 3.35vw;
      top: ${(props) => props.theme.space.marginMobile / 1.3}vw;
    `}
  font-family: ${(props) =>
    props.sale ? props.theme.fontFamily.bold : props.theme.fontFamily.normal};
  position: absolute;
  color: ${(props) =>
    props.new ||
    props.comingSoon ||
    props.limitedEdition ||
    props.twoPack ||
    props.specialEdition ||
    props.giftWrapping
      ? props.theme.colors.blue
      : props.theme.colors.white};
  z-index: 2;
  line-height: ${(props) =>
    props.sale ? props.theme.space.marginMobile * 2 : 5.5}vw;
  text-align: center;
  border-radius: ${(props) => (props.sale ? 10 : 100)}vw;

  .featured-collection & {
    top: -${(props) => props.theme.space.marginMobile}vw;
    right: -${(props) => props.theme.space.marginMobile}vw;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    top: ${(props) => props.theme.space.marginDesktop * 2}vw;
    right: ${(props) => props.theme.space.marginDesktop * 2}vw;
    font-size: ${(props) => props.theme.fontSize.d12};
    padding: 0 ${desktopVW(25)};
    line-height: ${(props) =>
      props.sale ? props.theme.space.marginDesktop * 5 : 2.1}vw;

    .featured-collection & {
      top: ${(props) => props.theme.space.marginDesktop * -4}vw;
      right: ${(props) => props.theme.space.marginDesktop * -4}vw;
    }
  }
`
